/** DESENUL paginii «Structura fațadei» — partea PURĂ: scara culorilor
 *  presiunii, așezarea fețelor și a planului în SVG. Fără React, ca să poată
 *  fi probată. */

/** Culoarea unei presiuni nete: roșu când apasă (+), albastru când trage (−),
 *  tot mai închis cu modulul; `wMax` e cea mai mare valoare absolută de pe
 *  desen, ca scara să fie aceeași pe toate fețele unui corp. */
export function culoarePresiune(w: number, wMax: number): string {
  const t = Math.min(1, Math.abs(w) / Math.max(wMax, 1e-6));
  // de la aproape alb la saturat, liniar în t
  const l = Math.round(96 - 56 * t);           // luminozitate 96 % → 40 %
  const s = Math.round(30 + 60 * t);
  return `hsl(${w >= 0 ? 8 : 212} ${s}% ${l}%)`;
}

/** Textul de pe celulă: negru pe deschis, alb pe închis. */
export const culoareText = (w: number, wMax: number): string =>
  Math.abs(w) / Math.max(wMax, 1e-6) > 0.55 ? "#fff" : "#1d1f22";

export interface ScaraFata {
  /** pixeli pe metru */
  px: number;
  /** lățimea și înălțimea desenului, în px */
  w: number;
  h: number;
}

/** Scara unei elevații: fața încape pe `latimeMax` px, dar nu mai înaltă
 *  decât `inaltimeMax`; o față de 100 m cu 30 m înălțime iese lizibilă. */
export function scaraFata(lungimeM: number, inaltimeM: number, latimeMax = 980, inaltimeMax = 300): ScaraFata {
  const px = Math.max(0.5, Math.min(latimeMax / Math.max(lungimeM, 1), inaltimeMax / Math.max(inaltimeM, 1)));
  return { px, w: Math.ceil(lungimeM * px), h: Math.ceil(inaltimeM * px) };
}

export interface CadruPlan {
  scara: number;
  /** `(x, y)` ale documentului → `(X, Y)` în SVG (y în sus în document, în jos în SVG) */
  la: (x: number, y: number) => [number, number];
  w: number;
  h: number;
}

/** Planul corpului într-o cutie de `marime` px, cu o margine pentru săgeți. */
export function cadruPlan(poly: readonly (readonly number[])[], marime = 240, margine = 44): CadruPlan {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of poly) {
    minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
    minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]);
  }
  const lat = Math.max(maxX - minX, 1), inalt = Math.max(maxY - minY, 1);
  const scara = (marime - 2 * margine) / Math.max(lat, inalt);
  const w = Math.ceil(lat * scara + 2 * margine), h = Math.ceil(inalt * scara + 2 * margine);
  return {
    scara, w, h,
    la: (x, y) => [margine + (x - minX) * scara, h - margine - (y - minY) * scara],
  };
}

/** Numărul cu două zecimale și semn, pentru celule și tabele. */
export const kpa = (w: number): string => (w > 0 ? "+" : "") + w.toFixed(2);

/** Cea mai mare presiune în modul de pe un corp — scara culorilor lui. */
export function maximulCorpului(fete: readonly { niveluri: readonly { celule: readonly { w_pres: number; w_succ: number;
                                  w_pres_1m2: number; w_succ_1m2: number }[] }[] }[]): number {
  let m = 0;
  for (const f of fete) for (const n of f.niveluri) for (const c of n.celule) {
    m = Math.max(m, Math.abs(c.w_pres), Math.abs(c.w_succ), Math.abs(c.w_pres_1m2), Math.abs(c.w_succ_1m2));
  }
  return m;
}

/** Culoarea unei categorii de teren pe roza sectoarelor: ca pe o hartă —
 *  marea și lacul albastre, câmpul deschis verde-pal, suburbia și pădurea
 *  bej, orașul gri-ardezie. Necunoscutul e alb. */
export function culoareTeren(cat: string | null | undefined): string {
  switch (cat) {
    case "0": return "#60a5fa";
    case "I": return "#bfdbfe";
    case "II": return "#e2f0b8";
    case "III": return "#d6cdbf";
    case "IV": return "#8b96a8";
    default: return "#fff";
  }
}

/** Punctul la azimutul `az` (grade de la nord, în sensul acelor) și raza `r`
 *  în jurul centrului `(cx, cy)` al unui SVG (y în jos). */
export const laAzimut = (cx: number, cy: number, r: number, az: number): [number, number] =>
  [cx + r * Math.sin((az * Math.PI) / 180), cy - r * Math.cos((az * Math.PI) / 180)];

/** Pana sectorului dintre azimuturile `az0` și `az1` (az1 după az0, în sensul
 *  acelor, cel mult 180°), ca `d` de `<path>`. */
export function panaSvg(cx: number, cy: number, r: number, az0: number, az1: number): string {
  const [x0, y0] = laAzimut(cx, cy, r, az0);
  const [x1, y1] = laAzimut(cx, cy, r, az1);
  return `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

/** Adâncimea unui profil din numele lui din catalog («CW50-M-175» → 175);
 *  `null` când numele n-o spune. */
export function adancimeProfil(nume: string | null | undefined): number | null {
  const m = /-(\d+)$/.exec(nume ?? "");
  return m ? Number(m[1]) : null;
}

export interface RezumatCorp {
  wPres1: number;
  wSucc1: number;
  /** cea mai mare forță pe o față, în modul, kN */
  fMax: number;
  /** profilul de montant cel mai adânc cerut, sau null fără cortină */
  profil: string | null;
  areCortina: boolean;
  /** toate etajele tuturor porțiunilor au montant și traversă din catalog */
  trece: boolean;
}

/** Cifrele unui corp pentru tabelul de alegere: presiunile pe 1 m², forța cea
 *  mai mare pe o față, profilul guvernant și dacă totul trece. */
export function rezumatCorp(corp: {
  are_cortina: boolean;
  presiuni: { fete: readonly { w_pres_1m2_max: number; w_succ_1m2_max: number; F_pres_kn: number; F_succ_kn: number }[] };
  cortina: { portiuni: readonly { niveluri: readonly { guvernant: { profil: string | null } | null;
                                                         traversa: { profil: string | null } | null }[] }[];
             profile_montanti: Record<string, number> };
}): RezumatCorp {
  let wPres1 = 0, wSucc1 = 0, fMax = 0;
  for (const f of corp.presiuni.fete) {
    wPres1 = Math.max(wPres1, f.w_pres_1m2_max);
    wSucc1 = Math.min(wSucc1, f.w_succ_1m2_max);
    fMax = Math.max(fMax, Math.abs(f.F_pres_kn), Math.abs(f.F_succ_kn));
  }
  let profil: string | null = null;
  for (const nume of Object.keys(corp.cortina.profile_montanti)) {
    if ((adancimeProfil(nume) ?? -1) > (adancimeProfil(profil) ?? -1)) profil = nume;
  }
  const trece = corp.cortina.portiuni.every((p) => p.niveluri.every((n) => !!n.guvernant?.profil && !!n.traversa?.profil));
  return { wPres1, wSucc1, fMax, profil, areCortina: corp.are_cortina, trece };
}
